import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Clock, PhoneCall } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import CtaBand from "@/components/home/CtaBand";
import { ARTICLES, ARTICLE_MAP } from "@/data/articles";
import { FINANCE_PAGE_LABELS } from "@/data/financePages";
import { COMPANY } from "@/data/content";

const DEFAULT_TITLE = "Commercial Mortgage Broker Melbourne | Invesmo";
const DEFAULT_DESC = "Explore commercial property, development and practice finance with Invesmo. Finance enquiries for doctors, accountants and professionals in Melbourne.";

export default function Article() {
  const { slug } = useParams();
  const article = ARTICLE_MAP[slug];

  useEffect(() => {
    if (!article) return;
    document.title = article.metaTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", article.metaDescription);
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "article-ld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.metaDescription,
      author: { "@type": "Person", name: "Anu Prakash" },
      publisher: { "@type": "Organization", name: "Invesmo Pty Ltd" },
      mainEntityOfPage: `https://www.invesmo.com.au/resources/${article.slug}`,
    });
    document.head.appendChild(ld);
    return () => {
      document.title = DEFAULT_TITLE;
      if (meta) meta.setAttribute("content", DEFAULT_DESC);
      document.getElementById("article-ld")?.remove();
    };
  }, [article]);

  if (!article) return <Navigate to="/resources" replace />;

  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 2);

  return (
    <div data-testid={`article-${slug}`}>
      <article className="pt-36 sm:pt-44 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Reveal>
            <Link to="/resources" data-testid="article-back" className="inline-flex items-center gap-2 text-sm font-semibold text-ink/45 hover:text-gold-700 transition-colors mb-10">
              <ArrowLeft size={15} /> All resources
            </Link>
            <Eyebrow>{article.category}</Eyebrow>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-ink leading-[1.1]" data-testid="article-title">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 mt-6 text-xs text-ink/45">
              <span>By Anu Prakash</span>
              <span>·</span>
              <span>{article.date}</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1.5"><Clock size={12} />{article.read}</span>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-12 space-y-6" data-testid="article-body">
              {article.body.map((block, i) => {
                if (block.h2) {
                  return (
                    <h2 key={i} className="font-display text-2xl font-bold text-ink pt-6 flex items-center gap-3">
                      <span className="h-px w-7 bg-gold shrink-0" />{block.h2}
                    </h2>
                  );
                }
                if (block.list) {
                  return (
                    <ul key={i} className="space-y-3 rounded-2xl bg-white border border-ink/8 p-6">
                      {block.list.map((li) => (
                        <li key={li} className="flex items-start gap-3 text-[15px] text-ink/70 leading-relaxed">
                          <Check size={16} className="text-brand mt-1 shrink-0" strokeWidth={2.5} />
                          {li}
                        </li>
                      ))}
                    </ul>
                  );
                }
                return <p key={i} className="text-ink/65 leading-[1.85] text-[15px] sm:text-base">{block.p}</p>;
              })}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-14 rounded-3xl bg-obsidian text-white p-8 sm:p-10 grain relative overflow-hidden" data-testid="article-cta">
              <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-gold/15 blur-3xl pointer-events-none" />
              <h2 className="font-display text-2xl font-bold mb-3">Talk it through with Anu</h2>
              <p className="text-white/60 text-sm leading-relaxed max-w-md mb-7">
                Reading about it only gets you so far. A short call answers the questions your scenario actually raises.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to={`/callback?utm_source=article-${slug}`} data-testid="article-callback-cta" className="inline-flex items-center gap-2 rounded-full bg-gold text-obsidian px-7 py-3.5 text-sm font-bold transition-all duration-300 hover:shadow-gold-glow">
                  <PhoneCall size={14} /> Request a callback
                </Link>
                <Link to={`/${article.related}`} data-testid="article-related-service" className="rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white/80 transition-all duration-300 hover:border-gold hover:text-gold">
                  {FINANCE_PAGE_LABELS[article.related]}
                </Link>
              </div>
            </div>
            <p className="text-[11px] text-ink/40 leading-relaxed mt-8">
              General information only — not financial, legal or tax advice. Consider your objectives, financial
              situation and needs, and seek professional advice before acting. Credit subject to lender assessment,
              terms, conditions, fees and charges. {COMPANY.name}, Credit Representative {COMPANY.crn} of {COMPANY.aggregator} (ACL {COMPANY.acl}).
            </p>
          </Reveal>

          <Reveal delay={0.2}>
            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-ink/40 mt-14 mb-5">Keep reading</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              {related.map((r) => (
                <Link key={r.slug} to={`/resources/${r.slug}`} data-testid={`article-more-${r.slug}`} className="group rounded-2xl bg-white border border-ink/8 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-gold/60">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold-700">{r.category}</span>
                  <div className="font-display font-bold text-ink mt-2 leading-snug group-hover:text-gold-700 transition-colors">{r.title}</div>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </article>

      <CtaBand />
    </div>
  );
}
